const currencyFormatter = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

export const formatCurrency = (value = 0, compact = false) => {
  const numericValue = typeof value === 'number' ? value : Number(value) || 0
  // Runde auf volle Euro (keine Nachkommastellen)
  const roundedValue = Math.round(numericValue)
  
  // Kompakte Darstellung für mobile Ansicht (z.B. "540k €", "1,2M €")
  if (compact) {
    const absValue = Math.abs(roundedValue)
    const sign = roundedValue < 0 ? '-' : ''
    if (absValue >= 1000000) {
      const millions = absValue / 1000000
      return `${sign}${millions.toFixed(millions >= 10 ? 0 : 1).replace('.', ',')}M €`
    }
    if (absValue >= 1000) {
      const thousands = absValue / 1000
      return `${sign}${thousands.toFixed(thousands >= 100 ? 0 : 0)}k €`
    }
    return `${sign}${absValue} €`
  }
  
  return currencyFormatter.format(roundedValue)
}

export const formatMonthToYearMonth = (month = 1) => {
  const safeMonth = Number.isFinite(month) ? Math.max(1, Math.floor(month)) : 1
  const year = Math.floor((safeMonth - 1) / 12) + 1
  const monthNum = ((safeMonth - 1) % 12) + 1
  return `Jahr ${year} Monat ${monthNum} (M#${safeMonth})`
}

/**
 * Gibt die Anzahl der Tage in einem Monat zurück (berücksichtigt Schaltjahre)
 * @param {number} year - Jahr
 * @param {number} month - Monat (1-12)
 * @returns {number} - Anzahl der Tage im Monat
 */
const getDaysInMonth = (year, month) => {
  return new Date(year, month, 0).getDate()
}

/**
 * Berechnet das echte Datum basierend auf Startdatum, Monat und Tag seit Start
 * @param {number} month - Monat seit Spielstart (1 = Startmonat)
 * @param {number} day - Tag seit Spielstart (wird zu echtem Tag konvertiert)
 * @param {Object} startDate - { year: 2012, month: 1, day: 27 }
 * @returns {Object} - { year: 2012, month: 1, day: 27, monthName: 'Januar', fullDate: '27. Januar 2012', daysInMonth: 31 }
 */
export const getRealDate = (month = 1, day = 1, startDate = { year: 2012, month: 1, day: 27 }) => {
  const safeMonth = Number.isFinite(month) ? Math.max(1, Math.floor(month)) : 1
  const safeDay = Number.isFinite(day) ? Math.max(1, Math.floor(day)) : 1
  const startYear = startDate?.year || 2012
  const startMonth = startDate?.month || 1
  const startDay = startDate?.day || 1
  
  // Berechne Gesamtmonate seit Start
  const totalMonths = startMonth - 1 + (safeMonth - 1) // -1 weil month=1 der Startmonat ist
  
  // Berechne Jahr und Monat
  let currentYear = startYear + Math.floor(totalMonths / 12)
  let currentMonth = (totalMonths % 12) + 1 // +1 weil Monate 1-12 sind
  
  // Berechne Tag: Starttag + (Tag seit Start - 1)
  // Tag seit Start beginnt bei 1, also müssen wir -1 rechnen
  let currentDay = startDay + (safeDay - 1)
  
  // Wenn Tag über Monatsende hinausgeht, zum nächsten Monat
  const daysInMonth = getDaysInMonth(currentYear, currentMonth)
  while (currentDay > daysInMonth) {
    currentDay -= daysInMonth
    currentMonth += 1
    if (currentMonth > 12) {
      currentMonth = 1
      currentYear += 1
    }
  }
  
  const monthNames = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ]
  
  const finalDaysInMonth = getDaysInMonth(currentYear, currentMonth)
  
  return {
    year: currentYear,
    month: currentMonth,
    day: currentDay,
    monthName: monthNames[currentMonth - 1] || 'Unbekannt',
    fullDate: `${currentDay}. ${monthNames[currentMonth - 1] || 'Unbekannt'} ${currentYear}`,
    daysInMonth: finalDaysInMonth
  }
}

export const clamp = (value, min, max) => {
  const safeMin = typeof min === 'number' ? min : 0
  const safeMax = typeof max === 'number' ? max : safeMin
  const numericValue = typeof value === 'number' ? value : Number(value) || 0
  return Math.max(safeMin, Math.min(safeMax, numericValue))
}

/**
 * Korrigiert fehlerhafte Encoding-Werte (UTF-8 Encoding-Probleme)
 * Ersetzt falsch kodierte Umlaute durch korrekte Zeichen
 * @param {string} text - Text mit möglichen Encoding-Fehlern
 * @returns {string} - Korrigierter Text
 */
export const fixEncoding = (text) => {
  if (!text || typeof text !== 'string') return text
  return text
    .replace(/Ã¤/g, 'ä')
    .replace(/Ã¶/g, 'ö')
    .replace(/Ã¼/g, 'ü')
    .replace(/Ãœ/g, 'Ü')
    .replace(/Ã–/g, 'Ö')
    .replace(/Ã„/g, 'Ä')
    .replace(/ÃŸ/g, 'ß')
    .replace(/Â·/g, '·')
}

/**
 * Prüft, ob ein Jahr ein Schaltjahr ist
 * @param {number} year - Jahr
 * @returns {boolean} - true wenn Schaltjahr
 */
export const isLeapYear = (year) => {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0)
}

/**
 * Berechnet die anteilige Miete für den Restmonat ab einem Starttag
 * @param {number} monthlyRent - Monatliche Miete
 * @param {number} startDay - Starttag (1-basiert)
 * @param {number} month - Monat (1-12)
 * @param {number} year - Jahr
 * @returns {number} - Anteilige Miete für den Restmonat
 */
export const calculateProratedRent = (monthlyRent, startDay, month, year) => {
  const daysInMonth = getDaysInMonth(year, month)
  const remainingDays = daysInMonth - startDay + 1 // +1 weil Starttag mitgezählt wird
  return Math.round((monthlyRent / daysInMonth) * remainingDays)
}

/**
 * Prüft, ob der aktuelle Tag der letzte Tag im Monat ist
 * @param {number} day - Aktueller Tag
 * @param {number} month - Monat (1-12)
 * @param {number} year - Jahr
 * @returns {boolean} - true wenn letzter Tag im Monat
 */
export const isLastDayOfMonth = (day, month, year) => {
  return day === getDaysInMonth(year, month)
}


